import { useQuery } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { useTranslation } from 'react-i18next';
import { CalendarClock, MapPin, Clock, User } from 'lucide-react';
import { PremiumPageHeader } from '@repo/ui';

const DAY_LABELS: Record<string, string> = {
  sunday: 'days.SUNDAY',
  monday: 'days.MONDAY',
  tuesday: 'days.TUESDAY',
  wednesday: 'days.WEDNESDAY',
  thursday: 'days.THURSDAY',
  friday: 'days.FRIDAY',
  saturday: 'days.SATURDAY',
};

const SchedulePage = () => {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ['student-schedule'],
    queryFn: () => api.get('/student/schedule').then((res) => res.data?.data),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const weeklySchedule = data?.weeklySchedule ?? [];
  const upcomingSessions = data?.upcomingSessions ?? [];

  // Group weekly schedule by day
  const byDay: Record<string, any[]> = {};
  for (const item of weeklySchedule) {
    const dayKey = item.day.toLowerCase();
    if (!byDay[dayKey]) byDay[dayKey] = [];
    byDay[dayKey].push(item);
  }

  return (
    <div className="space-y-6">
      <PremiumPageHeader
        title={t('student.schedule.title')}
        subtitle={t('student.schedule.subtitle', {
          defaultValue: 'Monitor your weekly sessions and upcoming appointments',
        })}
        icon={CalendarClock}
        iconClassName="text-primary"
        iconContainerClassName="bg-primary/10 border-primary/20"
      />

      {/* Weekly Recurring Schedule */}
      {Object.keys(byDay).length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">{t('student.schedule.weekly')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(byDay).map(([day, items]) => (
              <div
                key={day}
                className="rounded-xl border border-border bg-card shadow-sm overflow-hidden"
              >
                <div className="px-4 py-3 bg-primary/10 font-semibold text-primary">
                  {t(DAY_LABELS[day] || day)}
                </div>
                <div className="p-4 space-y-3">
                  {items.map((item: any, i: number) => (
                    <div key={i} className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{item.group.name}</p>
                        <p className="text-xs text-muted-foreground">{item.group.code}</p>
                      </div>
                      <div className="text-end shrink-0">
                        <p className="text-sm font-medium flex items-center gap-1 justify-end">
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          {item.startTime} — {item.endTime}
                        </p>
                        {item.branch && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                            <MapPin className="h-3 w-3" />
                            {item.branch.nameEn}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Sessions */}
      {upcomingSessions.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">{t('student.schedule.upcoming')}</h2>
          <div className="space-y-3">
            {upcomingSessions.map((session: any) => (
              <div
                key={session._id}
                className="rounded-xl border border-border bg-card p-5 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-medium truncate">
                      {session.groups?.map((g: any) => g.name).join(', ')}
                    </p>
                    <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1">
                        <CalendarClock className="h-3.5 w-3.5" />
                        {new Date(session.startDate).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {new Date(session.startDate).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {session.host && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <User className="h-3.5 w-3.5" />
                        {session.host.name}
                      </div>
                    )}
                    {session.deliveryMode && (
                      <span className="text-xs px-3 py-1 rounded-full bg-primary/10 text-primary font-medium">
                        {session.deliveryMode}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!Object.keys(byDay).length && !upcomingSessions.length && (
        <div className="text-center py-16 text-muted-foreground">{t('student.schedule.empty')}</div>
      )}
    </div>
  );
};

export default SchedulePage;
